import { useState, useRef } from "react";
import { Mui } from '@osu/react-ui';

let timer = undefined;
let isRunning = false;

// Matches both CRA (/static/js/main*.chunk.js) and Vite (/assets/index-*.js) entry points
const ENTRY_PATTERNS = [
    /\/assets\/index-[A-Za-z0-9_-]+\.js/,   // Vite
    /\/static\/js\/main[.]?[A-Za-z0-9]{0,10}\.chunk\.js/,  // CRA
];

function findEntryScript() {
    const scripts = Array.from(document.querySelectorAll('script[src]'));
    for (const pattern of ENTRY_PATTERNS) {
        const match = scripts.find(s => pattern.test(s.src));
        if (match) {
            const url = new URL(match.src, window.location.origin);
            return url.pathname;
        }
    }
    return null;
}

function findEntryInHtml(html) {
    for (const pattern of ENTRY_PATTERNS) {
        const match = html.match(pattern);
        if (match) return match[0];
    }
    return null;
}

function RefreshPageAlert() {
    const styles = { alignItems: "center" };
    const currentLoadedFilename = useRef(findEntryScript());
    const [versionsMatch, setVersionsMatch] = useState(true);
    const [closedForcedRefreshMessage, setClosed] = useState(false);
        
    javascriptVersionChecker(currentLoadedFilename.current, setVersionsMatch);
    const wasForcedRefresh = window.location.search.includes("reloaded=true") && !closedForcedRefreshMessage;
    const showSnackbarVersionMismatch = !versionsMatch;
    const showSnackbarValidationError = wasForcedRefresh && !closedForcedRefreshMessage;
    
    const action = (
        <div className="display-flex justify-content-space-evenly align-items-center">
            <Mui.Link component="button" onClick={() => window.location.reload()} className="margin-right-2">Refresh Page</Mui.Link>
        </div>
    );
    
    return (
        <>
            <Mui.Snackbar open={showSnackbarValidationError} TransitionComponent={Mui.Slide} anchorOrigin={{ vertical: 'top', horizontal: 'center', }}>
                <Mui.Alert onClose={() => setClosed(true)} severity="warning" style={styles}>Website changes detected, page has been reloaded</Mui.Alert>
            </Mui.Snackbar>
            
            <Mui.Snackbar open={showSnackbarVersionMismatch} TransitionComponent={Mui.Slide} anchorOrigin={{ vertical: 'top', horizontal: 'center', }}>
                <Mui.Alert action={action} severity="warning" style={styles}>Website changes detected</Mui.Alert>
            </Mui.Snackbar>
        </>
    );
}

function javascriptVersionChecker(currentLoadedFilename, setVersionsMatch) {
    // Skip version checking in development — hot reload changes filenames constantly
    if (import.meta.env.DEV) { return; }
    // If we couldn't identify the current entry script, skip checking to avoid false positives
    if (!currentLoadedFilename) { return; }

    const seconds = 60 * 10; // 10 minutes
    const intervalMs = seconds * 1000;
    
    clearInterval(timer);
    timer = setInterval(() => checkJavascriptVersion(currentLoadedFilename, setVersionsMatch), intervalMs);
}

async function checkJavascriptVersion(currentLoadedFilename, setVersionsMatch) {
    if (isRunning) { return; } // Prevent simultaneous runs
    
    isRunning = true;
    
    try {
        const latestHtml = await (await fetch("/")).text();
        const newestVersion = findEntryInHtml(latestHtml);

        // If we can't find an entry script in the fetched HTML, skip — don't false-positive
        if (!newestVersion) { isRunning = false; return; }
        
        const javascriptFilenamesMatch = newestVersion === currentLoadedFilename;
        if (javascriptFilenamesMatch) { isRunning = false; return; }
        
        console.warn(`New main version detected (current=${currentLoadedFilename} new=${newestVersion}), prompting for refresh`);
        setVersionsMatch(false);
        clearInterval(timer);
    } catch (exception) {
        console.error("Error while attempting to fetch homepage for javascript version check");
    }
    
    isRunning = false;
}

export default RefreshPageAlert;